import React from "react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { IMG } from "@/data/content";
import { useContent } from "@/context/ContentContext";
import { Quote, Star } from "lucide-react";

const resolveImg = (v) => (v && typeof v === "string" && v.startsWith("http") ? v : IMG[v] || v);
const formatDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; }
};

const StarRow = ({ n = 5 }) => (
  <div className="flex items-center gap-0.5 text-gold">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`h-3.5 w-3.5 ${i < n ? "fill-gold" : "opacity-30"}`} strokeWidth={0} />
    ))}
  </div>
);

const Testimonials = () => {
  const { content } = useContent();
  const all = (content.testimonials || [])
    .filter((t) => t.visible !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div data-testid="testimonials-page">
      <PageHero
        eyebrow="Success Stories"
        title={<>Lives the Kalpataru has <em>quietly</em> changed.</>}
        subtitle="Every story is real, witnessed and shared with consent. Names initialled where privacy was requested."
        image={IMG.forest}
      />

      <section className="section">
        <div className="wrap grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {all.map((t, i) => (
            <Reveal key={t.name + i} delay={i * 60}>
              <article className="glass lift p-8 h-full relative" data-testid={`testimonial-${i}`}>
                <Quote className="absolute right-6 top-6 h-12 w-12 text-gold/15" strokeWidth={1} />
                {t.rating && <StarRow n={Number(t.rating) || 5} />}
                <p className={`${t.rating ? "mt-4" : ""} font-display italic text-lg leading-snug text-ivory/90`}>
                  "{t.quote}"
                </p>
                <div className="mt-6 flex items-center gap-4">
                  {t.avatar ? (
                    <img src={resolveImg(t.avatar)} alt={t.name} className="h-12 w-12 rounded-full object-cover ring-1 ring-gold/40" />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold font-display text-lg">
                      {(t.name || "?").charAt(0)}
                    </div>
                  )}
                  <div>
                    <div className="text-ivory">{t.name}</div>
                    <div className="text-xs uppercase tracking-[0.2em] text-ivory/50">
                      {[t.service, t.role, t.country].filter(Boolean).join(" · ")}
                    </div>
                    {t.date && <div className="text-[10px] uppercase tracking-[0.25em] text-ivory/40 mt-1">{formatDate(t.date)}</div>}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        {all.length === 0 && (
          <div className="wrap text-center text-ivory/55 py-20">
            Stories will appear here as they arrive.
          </div>
        )}
      </section>
    </div>
  );
};

export default Testimonials;
